/**
 * Centralized validation messages and rules.
 *
 * Contains all validation patterns, rules, and error messages used across forms.
 * When validation requirements change, update them here.
 *
 * @example
 * import { validation } from '@/constants';
 *
 * if (!validation.patterns.email.test(value)) {
 *   setError(validation.messages.email.invalid);
 * }
 *
 * const { valid, errors } = validatePassword(password);
 */

/**
 * Validation patterns (regex)
 */
export const validationPatterns = {
  /** Email pattern - basic format check */
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  /** Phone pattern - 7+ digits, optional country code, allows common separators */
  phone: /^\+?[\d\s\-().]{7,}$/,

  /** URL pattern - requires http(s) protocol */
  url: /^https?:\/\/.+/,

  /** Alphanumeric only */
  alphanumeric: /^[a-zA-Z0-9]+$/,

  /** Letters only */
  lettersOnly: /^[a-zA-Z]+$/,

  /** Numbers only */
  numbersOnly: /^\d+$/,

  /** No special characters (letters, numbers, spaces) */
  noSpecialChars: /^[a-zA-Z0-9\s]+$/,

  /** Contains lowercase letter */
  hasLowercase: /[a-z]/,

  /** Contains uppercase letter */
  hasUppercase: /[A-Z]/,

  /** Contains number */
  hasNumber: /\d/,

  /** Contains special character */
  hasSpecialChar: /[#$^+=!*()@%&]/,
} as const;

/**
 * Password validation configuration
 */
export const passwordConfig = {
  minLength: 8,
  maxLength: 50,
  /** Characters considered "special" for password requirements */
  specialChars: '#$^+=!*()@%&',
} as const;

/**
 * Password validation rules with test functions and messages
 */
export const passwordRules = {
  length: {
    test: (password: string) =>
      password.length >= passwordConfig.minLength &&
      password.length <= passwordConfig.maxLength,
    message: `Password must be ${passwordConfig.minLength}-${passwordConfig.maxLength} characters`,
  },
  lowercase: {
    test: (password: string) => validationPatterns.hasLowercase.test(password),
    message: 'Password must contain at least one lowercase letter',
  },
  uppercase: {
    test: (password: string) => validationPatterns.hasUppercase.test(password),
    message: 'Password must contain at least one uppercase letter',
  },
  number: {
    test: (password: string) => validationPatterns.hasNumber.test(password),
    message: 'Password must contain at least one number',
  },
  special: {
    test: (password: string) => validationPatterns.hasSpecialChar.test(password),
    message: `Password must contain at least one special character (${passwordConfig.specialChars})`,
  },
} as const;

/**
 * Validation error messages - parameterized for flexibility
 */
export const validationMessages = {
  /** Generic required field */
  required: (field: string) => `${field} is required`,

  /** Length constraints */
  minLength: (field: string, min: number) =>
    `${field} must be at least ${min} characters`,
  maxLength: (field: string, max: number) =>
    `${field} must be at most ${max} characters`,
  exactLength: (field: string, length: number) =>
    `${field} must be exactly ${length} characters`,

  /** Range constraints */
  minValue: (field: string, min: number) => `${field} must be at least ${min}`,
  maxValue: (field: string, max: number) => `${field} must be at most ${max}`,
  range: (field: string, min: number, max: number) =>
    `${field} must be between ${min} and ${max}`,

  /** Email validation */
  email: {
    invalid: 'Please enter a valid email address',
    duplicate: 'This email is already in use',
    notFound: 'No account found with this email',
    alreadyVerified: 'This email is already verified',
  },

  /** Phone validation */
  phone: {
    invalid: 'Please enter a valid phone number',
    duplicate: 'This phone number is already in use',
    tooShort: 'Phone number is too short',
    tooLong: 'Phone number is too long',
  },

  /** Password validation */
  password: {
    mismatch: 'Passwords do not match',
    weak: 'Password does not meet requirements',
    tooShort: `Password must be at least ${passwordConfig.minLength} characters`,
    tooLong: `Password must be at most ${passwordConfig.maxLength} characters`,
    current: 'Please enter your current password',
    incorrect: 'Current password is incorrect',
    same: 'New password must be different from current password',
  },

  /** URL validation */
  url: {
    invalid: 'Please enter a valid URL',
    missingProtocol: 'URL must start with http:// or https://',
  },

  /** Selection validation */
  selection: {
    atLeastOne: (itemType: string) => `Please select at least one ${itemType}`,
    required: (itemType: string) => `A ${itemType} must be selected`,
    maxExceeded: (itemType: string, max: number) =>
      `You can only select up to ${max} ${itemType}s`,
  },

  /** File validation */
  file: {
    required: 'Please select a file',
    tooLarge: (maxMb: number) => `File size must be less than ${maxMb}MB`,
    invalidType: (types: string[]) => `File must be one of: ${types.join(', ')}`,
    tooMany: (max: number) => `You can only upload up to ${max} files`,
  },

  /** Date validation */
  date: {
    invalid: 'Please enter a valid date',
    future: 'Date must be in the future',
    past: 'Date must be in the past',
    before: (date: string) => `Date must be before ${date}`,
    after: (date: string) => `Date must be after ${date}`,
    range: (start: string, end: string) => `Date must be between ${start} and ${end}`,
  },

  /** Generic format errors */
  format: {
    invalid: (field: string) => `${field} format is invalid`,
    alphanumeric: (field: string) => `${field} must contain only letters and numbers`,
    lettersOnly: (field: string) => `${field} must contain only letters`,
    numbersOnly: (field: string) => `${field} must contain only numbers`,
  },

  /** Uniqueness validation */
  unique: {
    duplicate: (field: string) => `This ${field} is already taken`,
    exists: (field: string) => `A ${field} with this value already exists`,
  },

  /** Confirmation field */
  confirm: {
    mismatch: (field: string) => `${field} confirmation does not match`,
  },

  /** Hotspot question validation */
  hotspot: {
    imageRequired: 'Hotspot questions require an image.',
    regionsRequired: 'At least one clickable region is required.',
    correctRegionRequired: 'At least one region must be marked as correct.',
    regionTooSmall: 'Region is too small. Draw a larger area.',
  },

  /** Question validation */
  question: {
    textRequired: 'Question text is required.',
    choicesRequired: (min: number) => `At least ${min} choices are required.`,
    correctRequired: 'At least one choice must be marked as correct.',
  },
} as const;

/**
 * Validate a password against all rules
 * @returns Object with valid boolean and array of error messages
 */
export function validatePassword(password: string): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  for (const rule of Object.values(passwordRules)) {
    if (!rule.test(password)) {
      errors.push(rule.message);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Check if a string is a valid email
 */
export function isValidEmail(email: string): boolean {
  return validationPatterns.email.test(email);
}

/**
 * Check if a string is a valid phone number
 */
export function isValidPhone(phone: string): boolean {
  return validationPatterns.phone.test(phone);
}

/**
 * Check if a string is a valid URL
 */
export function isValidUrl(url: string): boolean {
  return validationPatterns.url.test(url);
}

/**
 * Combined validation export for convenience
 */
export const validation = {
  patterns: validationPatterns,
  password: {
    config: passwordConfig,
    rules: passwordRules,
    validate: validatePassword,
  },
  messages: validationMessages,
  isValidEmail,
  isValidPhone,
  isValidUrl,
} as const;
